import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useUpdateCompanyMutation,
  useUpdateCompanyDraftMutation,
  useUpdateCompanyDraftEditMutation,
  useFetchCompanyDraftEditMutation,
  useGetCompanyRequestStatusMutation,
  useUpdateCompanyRequestStatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import useCompanyUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { savePath, nextStatus } from "./companyDraftSave";

/* The About Business section's behaviour, lifted out of the markup. */

/* Transcribed verbatim from About.jsx's numberOfEmployees memo. */
export const EMPLOYEE_OPTIONS = [
  { value: "0-5", label: "0-5 Employees" },
  { value: "6-10", label: "6-10 Employees" },
  { value: "11-50", label: "11-50 Employees" },
  { value: "51-100", label: "51-100 Employees" },
  { value: "101-500", label: "101-500 Employees" },
  { value: "501-1000", label: "501-1000 Employees" },
  { value: "1000 - 10000", label: "1000 - 10000 Employees" },
  { value: "10000+", label: "10000+ Employees" },
];

/* The three-way save chain is shared with Social Media — see
   companyDraftSave.js. Re-exported here because this is where it started and
   the tests reach for it by this name. */
export { savePath };

/* Transcribed: a direct company write preserves "Requested" and otherwise
   marks the record Modified. */
export const nextCompanyStatus = (company) => nextStatus(company?.company_status);

export const useAboutSection = ({ onSaved }) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  const [employees, setEmployees] = useState("");
  const [description, setDescription] = useState("");
  /* The TRN input is commented out of the live form, but the value is still LOADED from the record and SUBMITTED on every save path. */
  const [trn, setTRN] = useState("");

  const [draft, setDraft] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("pending");
  const [requestID, setRequestID] = useState(null);
  /* Transcribed: set the moment the description differs from the record's, and
     it gates the draft refetch so an in-progress edit is not overwritten. */
  const [descriptionEdited, setDescriptionEdited] = useState("");

  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const [updateCompanyDraft] = useUpdateCompanyDraftMutation();
  const [updateCompanyDraftEdit] = useUpdateCompanyDraftEditMutation();
  const [fetchCompanyDraftEdit] = useFetchCompanyDraftEditMutation();
  const [getCompanyRequestStatus] = useGetCompanyRequestStatusMutation();
  const [updateCompanyRequestStatus] = useUpdateCompanyRequestStatusMutation();

  const handleUpdateCompanyFollow = useCompanyUpdate();

  const companyID = currentEditingCompany?.companyID;

  /* Transcribed: the draft's values win over the record's, falling back
     field by field. */
  const loadDraft = async () => {
    try {
      const fetched = await fetchCompanyDraftEdit({ companyId: companyID, token }).unwrap();
      if (fetched?.data) {
        const row = fetched.data[0];
        setDraft(row);
        setEmployees(row?.number_of_employees || currentEditingCompany?.number_of_employees);
        setDescription(row?.company_description || currentEditingCompany?.company_description);
        setTRN(row?.TRN_no || currentEditingCompany?.TRN_no);
      }
    } catch {
      /* Swallowed in the live code. Kept: with no draft the form simply shows
         the company record's own values, which the effect below has already
         put in place. */
    }
  };

  useEffect(() => {
    if (!currentEditingCompany) return;

    /* Transcribed exactly, including the shape. */
    if (descriptionEdited === "") {
      loadDraft();
    } else {
      setEmployees(currentEditingCompany?.number_of_employees);
      setTRN(currentEditingCompany?.TRN_no);
    }

    /* Transcribed: the latest request for THIS company decides the status the
       save chain branches on. */
    if (currentEditingCompany?.userCompany) {
      (async () => {
        const individualID =
          localStorage.getItem("userindividualID") ||
          currentEditingCompany?.userCompany?.[0]?.individualID?.individualID;

        const res = await getCompanyRequestStatus({
          companyID: currentEditingCompany.companyID,
          individualID,
          token,
        });

        const rows = res?.data?.filter(
          (item) => item?.companyID?.companyID === currentEditingCompany.companyID
        );

        if (rows?.length) {
          const latest = rows[rows.length - 1];
          setVerificationStatus(latest?.status);
          setRequestID(latest?.requestID);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany, verificationStatus]);

  /* Transcribed: an edit that matches the stored description is not an edit. */
  const onDescriptionChange = (value) => {
    if (currentEditingCompany?.company_description !== value) {
      setDescriptionEdited("Modified");
      setDescription(value);
    }
  };

  /* Transcribed from handleTRN — kept with the value, for whenever the field
     comes back. */
  const onTRNChange = (value) =>
    setTRN(String(value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase());

  const payload = () => ({
    number_of_employees: employees,
    company_description: description,
    company_status: "Modified",
    TRN_no: trn,
  });

  const submitDraft = async () => {
    /* Transcribed order: the outstanding request is closed BEFORE the draft is
       written, so a rejected close leaves no orphan draft. */
    await updateCompanyRequestStatus({ requestID, token }).unwrap();
    const res = await updateCompanyDraft({
      data: { companyID, ...payload() },
      token,
    }).unwrap();
    if (res) {
      setDescriptionEdited("");
      onSaved?.();
    }
  };

  const submitDraftEdit = async () => {
    const res = await updateCompanyDraftEdit({
      data: payload(),
      companyId: companyID,
      token,
      /* Transcribed: names the draft slice being edited. */
      type: "aboutcompany",
    }).unwrap();
    if (res) onSaved?.();
  };

  const submitCompany = async () => {
    const res = await updateCompany({
      data: {
        number_of_employees: employees,
        company_description: description,
        company_status: nextCompanyStatus(currentEditingCompany),
        TRN_no: trn,
      },
      token,
      companyId: companyID,
    }).unwrap();

    if (res) {
      dispatch(setCurrentEditingCompany(res));
      /* Transcribed: followers are told the About section changed. The live
         code logs a failure and carries on, which it must — a follower
         notification failing is no reason to block the save. */
      const result = await handleUpdateCompanyFollow(companyID, "About Updated", token);
      if (!result.success) console.error(result.error);
      onSaved?.();
    }
  };

  const submit = async () => {
    const path = savePath({ draft, verificationStatus });
    try {
      if (path === "draft") return await submitDraft();
      if (path === "draftEdit") return await submitDraftEdit();
      return await submitCompany();
    } catch (error) {
      /* The live code swallows each of these. */
      console.error("About section save failed:", error);
      return undefined;
    }
  };

  return {
    /* The record's own name — never editable here. */
    companyName: currentEditingCompany?.registered_company_name,
    employees,
    setEmployees,
    description,
    onDescriptionChange,
    trn,
    onTRNChange,
    draft,
    verificationStatus,
    /* Transcribed: the glyph beside the heading reads the DRAFT's status when
       there is a draft awaiting review, and the company's otherwise. */
    displayStatus:
      draft?.company_status === "raised" || draft?.company_status === "Modified"
        ? "Requested"
        : currentEditingCompany?.company_status,
    isLoading,
    submit,
  };
};

export default useAboutSection;
